import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, Check, X, RotateCcw, ChevronRight, Target } from 'lucide-react';
import api from '../../services/api';
import BrandLogo from '../../components/ui/BrandLogo';
import '../../css/admin/landingcodexclaude.css';
import '../../css/admin/landingclaudehybride.css';

/**
 * Prototype E — hybride de l'hybride (/landingclaudehybride).
 * Direction visuelle : le thème éditorial + hero photographique de Codex
 * (proto D), dont on garde les sections numérotées et les « satellites ».
 * Ajouts Claude : la courbe de l'oubli (panneau sombre, dessinée au scroll),
 * et un MINI-QUIZ de 3 questions dont le pool s'adapte à la provenance
 * (?theme=atsem, utm ou referrer → questions ATSEM ; sinon pool mélangé).
 */

// ---------- Pools du mini-audit ----------
// 6 questions = 2 par SUJET (3 sujets transverses) → à la fin, un bilan par
// sujet qui dit quoi travailler — la promesse de la section 04, mais vécue.

export const POOL_GENERIC = {
    label: 'Audit mélangé',
    mastery: [
        { label: 'Culture générale', pct: 86, tone: 'ok' },
        { label: 'Logique & raisonnement', pct: 74, tone: 'ok' },
        { label: 'Français', pct: 58, tone: 'warn' },
        { label: 'Institutions', pct: 31, tone: 'new' },
    ],
    questions: [
        {
            sujet: 'Logique & raisonnement',
            enonce: 'Complétez la suite : 2, 6, 12, 20, 30, … ?',
            reponses: [['a', '36'], ['b', '40'], ['c', '42'], ['d', '44']],
            bonne: 'c',
            explication: "Les écarts augmentent de 2 à chaque pas : +4, +6, +8, +10… donc +12 → 42.",
        },
        {
            sujet: 'Institutions',
            enonce: 'Combien la France compte-t-elle de communes, environ ?',
            reponses: [['a', '15 000'], ['b', '25 000'], ['c', '35 000'], ['d', '50 000']],
            bonne: 'c',
            explication: "La France compte près de 35 000 communes — un record européen, régulièrement interrogé en culture territoriale.",
        },
        {
            sujet: 'Français',
            enonce: 'Complétez : « Voici les consignes qu\'il a ___ ce matin. »',
            reponses: [['a', 'reçu'], ['b', 'reçue'], ['c', 'reçus'], ['d', 'reçues']],
            bonne: 'd',
            explication: "Avec l'auxiliaire avoir, le participe s'accorde avec le COD placé avant : « qu' » reprend « les consignes » (féminin pluriel) → reçues.",
        },
        {
            sujet: 'Logique & raisonnement',
            enonce: 'Complétez la suite : 1, 1, 2, 3, 5, 8, … ?',
            reponses: [['a', '11'], ['b', '12'], ['c', '13'], ['d', '15']],
            bonne: 'c',
            explication: "Chaque terme est la somme des deux précédents (suite de Fibonacci) : 5 + 8 = 13.",
        },
        {
            sujet: 'Institutions',
            enonce: 'Quelle est la durée du mandat d\'un maire ?',
            reponses: [['a', '4 ans'], ['b', '5 ans'], ['c', '6 ans'], ['d', '7 ans']],
            bonne: 'c',
            explication: "Le maire est élu par le conseil municipal pour la durée du mandat municipal : 6 ans.",
        },
        {
            sujet: 'Français',
            enonce: 'Complétez : « Ils se sont ___ la main. »',
            reponses: [['a', 'serré'], ['b', 'serrés'], ['c', 'serrée'], ['d', 'serrées']],
            bonne: 'a',
            explication: "Le COD est « la main », placé après le verbe : le participe reste invariable → « Ils se sont serré la main. »",
        },
    ],
};

export const POOL_ATSEM = {
    label: 'Audit spécial ATSEM',
    mastery: [
        { label: "Rôle de l'ATSEM", pct: 86, tone: 'ok' },
        { label: 'Sécurité', pct: 74, tone: 'ok' },
        { label: 'Hygiène et soins', pct: 58, tone: 'warn' },
        { label: 'Périscolaire', pct: 31, tone: 'new' },
    ],
    questions: [
        {
            sujet: 'Sécurité & surveillance',
            enonce: "Lors d'une transition vers le périscolaire, le professionnel attendu n'est pas présent. Que doit faire l'ATSEM ?",
            reponses: [
                ['a', "Attendre la fin de l'activité pour prévenir"],
                ['b', 'Sécuriser le groupe et alerter selon la procédure'],
                ['c', 'Confier les enfants à un adulte disponible'],
                ['d', 'Laisser les enfants rejoindre seuls la salle périscolaire'],
            ],
            bonne: 'b',
            explication: "Le relais doit être effectif : la surveillance est maintenue et l'absence est signalée aux responsables selon la procédure prévue.",
        },
        {
            sujet: 'Santé & PAI',
            enonce: "Un enfant couvert par un PAI présente une réaction allergique au goûter. Que fait l'ATSEM ?",
            reponses: [
                ['a', "Elle applique la conduite d'urgence prévue par le PAI et alerte"],
                ['b', "Elle donne un antihistaminique de l'armoire à pharmacie"],
                ['c', "Elle attend de voir si la réaction s'aggrave avant d'agir"],
                ['d', 'Elle appelle les parents et attend leurs instructions avant toute action'],
            ],
            bonne: 'a',
            explication: "Le PAI prévoit précisément la conduite à tenir : l'ATSEM l'applique et alerte — jamais d'initiative médicale personnelle.",
        },
        {
            sujet: 'Hygiène',
            enonce: "Pour gagner de la place, peut-on transvaser un produit d'entretien dans une petite bouteille étiquetée ?",
            reponses: [
                ['a', "Oui, si l'étiquette est lisible"],
                ['b', "Non : les produits restent dans leur emballage d'origine"],
                ['c', 'Oui, hors de portée des enfants'],
                ['d', 'Oui, à condition de diluer le produit au préalable'],
            ],
            bonne: 'b',
            explication: "Jamais de transvasement : l'emballage d'origine porte l'étiquetage réglementaire et évite toute confusion dangereuse, notamment avec un contenant alimentaire.",
        },
        {
            sujet: 'Sécurité & surveillance',
            enonce: "Après l'évacuation incendie, l'ATSEM pense qu'un enfant est peut-être resté aux toilettes. Que fait-elle ?",
            reponses: [
                ['a', 'Elle retourne rapidement vérifier elle-même'],
                ['b', 'Elle le signale immédiatement au point de rassemblement, sans retourner dans les locaux'],
                ['c', 'Elle attend le comptage pour en être sûre'],
                ['d', "Elle demande à un collègue d'aller vérifier dans les locaux"],
            ],
            bonne: 'b',
            explication: "On ne retourne jamais dans les locaux après l'évacuation : l'absence est signalée immédiatement aux responsables, qui alertent les secours.",
        },
        {
            sujet: 'Santé & PAI',
            enonce: "Un enfant a de la fièvre en cours de matinée. L'ATSEM peut-elle lui donner un médicament ?",
            reponses: [
                ['a', 'Oui, un antipyrétique adapté à son âge'],
                ['b', "Non, jamais hors protocole : elle alerte l'enseignant et la famille est contactée"],
                ['c', 'Oui, si les parents ont donné un accord oral'],
                ['d', 'Oui, une demi-dose par précaution en attendant la famille'],
            ],
            bonne: 'b',
            explication: "Aucune initiative médicale : hors protocole (PAI), l'ATSEM ne donne jamais de médicament. Elle surveille, alerte, et la famille est contactée.",
        },
        {
            sujet: 'Hygiène',
            enonce: 'Avant de servir le goûter, que doit vérifier l\'ATSEM en priorité ?',
            reponses: [
                ['a', 'Les dates de péremption et le respect de la chaîne du froid'],
                ['b', 'Que chaque enfant a bien rangé ses affaires'],
                ['c', 'Que le goûter plaît à tous les enfants'],
                ['d', 'Que les portions sont strictement identiques pour tous'],
            ],
            bonne: 'a',
            explication: "Sécurité alimentaire d'abord : dates de péremption, chaîne du froid — sans oublier le lavage des mains et les allergies connues (PAI).",
        },
    ],
};

// Provenance : ?theme=atsem, utm_* contenant « atsem », ou referrer ATSEM.
function usePool() {
    const { search } = useLocation();
    const [pool, setPool] = useState(POOL_GENERIC);
    useEffect(() => {
        const params = new URLSearchParams(search);
        const hints = [
            params.get('theme'), params.get('utm_campaign'), params.get('utm_content'),
            document.referrer,
        ].filter(Boolean).join(' ').toLowerCase();
        if (hints.includes('atsem')) setPool(POOL_ATSEM);
    }, [search]);
    return pool;
}

// ---------- Confettis (autonomes) ----------
function ConfettiBurst({ burst }) {
    if (!burst) return null;
    const colors = ['#7c5cff', '#65e6cf', '#2563eb', '#f59e0b', '#ec4899'];
    const pieces = Array.from({ length: 28 }, (_, i) => ({
        left: 6 + Math.random() * 88,
        delay: Math.random() * 0.3,
        rot: Math.random() * 360,
        color: colors[i % colors.length],
        drift: (Math.random() - 0.5) * 140,
    }));
    return (
        <div className="lch-confetti" aria-hidden="true" key={burst}>
            {pieces.map((p, i) => (
                <span key={i} style={{
                    left: `${p.left}%`, background: p.color,
                    animationDelay: `${p.delay}s`, '--rot': `${p.rot}deg`, '--drift': `${p.drift}px`,
                }} />
            ))}
        </div>
    );
}

// ---------- Mini-audit : quiz mélangé + bilan par sujet ----------
function bilanFromAnswers(questions, answers) {
    // Regroupe par sujet (ordre de première apparition) → { sujet, ok, total, pct }
    const map = new Map();
    questions.forEach((q, i) => {
        if (!map.has(q.sujet)) map.set(q.sujet, { sujet: q.sujet, ok: 0, total: 0 });
        const row = map.get(q.sujet);
        row.total += 1;
        if (answers[i]) row.ok += 1;
    });
    return Array.from(map.values()).map(r => ({ ...r, pct: Math.round((r.ok / r.total) * 100) }));
}

function verdictOf(pct) {
    if (pct >= 100) return { label: 'Solide', tone: 'good' };
    if (pct >= 50) return { label: 'À consolider', tone: 'mid' };
    return { label: 'À travailler', tone: 'weak' };
}

function MiniQuiz({ pool }) {
    const [idx, setIdx] = useState(0);
    const [picked, setPicked] = useState(null);
    const [answers, setAnswers] = useState([]);
    const [done, setDone] = useState(false);
    const [burst, setBurst] = useState(0);

    const q = pool.questions[idx];
    const total = pool.questions.length;
    const score = answers.filter(Boolean).length;
    const isCorrect = picked === q?.bonne;

    const next = () => {
        if (idx + 1 < total) { setIdx(idx + 1); setPicked(null); }
        else { setDone(true); if (score / total >= 0.65) setBurst(b => b + 1); }
    };
    const pick = (id) => {
        if (picked) return;
        setPicked(id);
        setAnswers(a => [...a, id === q.bonne]);
    };
    const restart = () => { setIdx(0); setPicked(null); setAnswers([]); setDone(false); };

    if (done) {
        const great = score / total >= 0.65;
        const bilan = bilanFromAnswers(pool.questions, answers);
        const weakest = [...bilan].sort((a, b) => a.pct - b.pct)[0];
        const allSolid = weakest.pct >= 100;
        return (
            <div className="ldc-question lch-quiz">
                <ConfettiBurst burst={burst} />
                <div className="lch-quiz-result">
                    <span className="lch-quiz-score">{score}<small>/{total}</small></span>
                    <strong>{great ? 'Beau début !' : 'C\'est exactement pour ça qu\'on s\'entraîne.'}</strong>

                    <div className="lch-bilan" aria-label="Bilan par sujet">
                        {bilan.map(row => {
                            const v = verdictOf(row.pct);
                            return (
                                <div key={row.sujet} className="lch-bilan-row">
                                    <span className="lch-bilan-sujet">{row.sujet}</span>
                                    <span className="lch-bilan-bar"><i className={`is-${v.tone}`} style={{ width: `${Math.max(row.pct, 6)}%` }} /></span>
                                    <span className={`lch-bilan-verdict is-${v.tone}`}>{v.label}</span>
                                </div>
                            );
                        })}
                    </div>

                    <p className="lch-bilan-next">
                        {allSolid
                            ? 'Rien à signaler sur cet échantillon — sur MonQuiz, on passerait à des questions plus difficiles pour continuer à progresser.'
                            : <>Séance suggérée : <strong>10 questions · {weakest.sujet}</strong> — c'est exactement ce que MonQuiz te propose après chaque quiz.</>}
                    </p>

                    <div className="lch-quiz-result-actions">
                        <Link to="/inscription" className="ldc-btn ldc-btn--dark">Continuer gratuitement <ArrowRight size={16} /></Link>
                        <button type="button" className="lch-quiz-restart" onClick={restart}><RotateCcw size={14} /> Rejouer</button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="ldc-question lch-quiz">
            <ConfettiBurst burst={burst} />
            <div className="ldc-question-topline">
                <span>{q.sujet} · {pool.label}</span>
                <span className="lch-quiz-steps">
                    {pool.questions.map((_, i) => (
                        <i key={i} className={i < idx ? 'is-past' : i === idx ? 'is-now' : ''} />
                    ))}
                    {idx + 1} / {total}
                </span>
            </div>
            <p className="ldc-question-title">{q.enonce}</p>
            <div className="ldc-answers">
                {q.reponses.map(([id, label]) => {
                    const selected = picked === id;
                    const good = picked && id === q.bonne;
                    const bad = selected && id !== q.bonne;
                    return (
                        <button
                            key={id} type="button"
                            className={`ldc-answer ${selected ? 'is-selected' : ''} ${good ? 'is-good' : ''} ${bad ? 'lch-answer-bad' : ''}`}
                            onClick={() => pick(id)}
                            disabled={Boolean(picked)}
                        >
                            <span>{id.toUpperCase()}</span>{label}
                            {good && <Check size={17} />}
                            {bad && <X size={17} />}
                        </button>
                    );
                })}
            </div>
            {picked && (
                <div className={`ldc-feedback ${isCorrect ? 'is-correct' : ''}`}>
                    <strong>{isCorrect ? 'Oui !' : 'Pas tout à fait…'}</strong>
                    <p>{q.explication}</p>
                    <button type="button" onClick={next}>
                        {idx + 1 < total ? <>Question suivante <ChevronRight size={14} /></> : <>Voir mon résultat <ChevronRight size={14} /></>}
                    </button>
                </div>
            )}
        </div>
    );
}

// ---------- Carte flottante « vivante » du hero ----------
// Joue en boucle une micro-scène produit (à revoir → session → notion acquise) :
// la preuve du produit dans le hero, pas de la déco. Partagée par les 2 hybrides.
const HERO_SCENES = 3;
export function HeroLiveCard() {
    const [scene, setScene] = useState(0);
    useEffect(() => {
        if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
        const t = setInterval(() => setScene(s => (s + 1) % HERO_SCENES), 3000);
        return () => clearInterval(t);
    }, []);
    return (
        <div className="ldc-hero-float ldc-live" aria-hidden="true">
            {scene === 0 && (
                <div className="ldc-live-scene">
                    <span>À revoir aujourd'hui</span>
                    <strong>6 notions</strong>
                    <div className="ldc-live-bars"><i /><i /><i /><i /><i /><i /></div>
                </div>
            )}
            {scene === 1 && (
                <div className="ldc-live-scene">
                    <span>Session en cours</span>
                    <strong>4 / 6 réponses</strong>
                    <div className="ldc-live-progress"><i /></div>
                </div>
            )}
            {scene === 2 && (
                <div className="ldc-live-scene">
                    <span className="ldc-live-ok"><Check size={11} /> Notion acquise</span>
                    <strong>Prochaine révision</strong>
                    <div className="ldc-live-dates"><b>J+1</b><b className="is-next">J+3</b><b>J+8</b></div>
                </div>
            )}
        </div>
    );
}

// ---------- Maîtrise par sujet (barres animées, données selon le pool) ----------
function MasteryCard({ pool }) {
    const ref = useRef(null);
    const [shown, setShown] = useState(false);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const io = new IntersectionObserver(([e]) => {
            if (e.isIntersecting) { setShown(true); io.disconnect(); }
        }, { threshold: 0.35 });
        io.observe(el);
        return () => io.disconnect();
    }, []);
    const priorite = pool.mastery.find(r => r.tone === 'warn') || pool.mastery[pool.mastery.length - 1];
    return (
        <div className={`lch-mastery ${shown ? 'is-in' : ''}`} ref={ref}>
            <div className="lch-mastery-head"><strong>Maîtrise par sujet</strong><span>cette semaine</span></div>
            {pool.mastery.map((r, i) => (
                <div key={r.label} className="lch-mastery-row">
                    <span>{r.label}</span>
                    <div className="lch-mastery-track">
                        <i className={`lch-mastery-fill lch-mastery-fill--${r.tone}`}
                           style={{ '--w': `${r.pct}%`, transitionDelay: `${0.15 + i * 0.15}s` }} />
                    </div>
                    <strong>{r.pct} %</strong>
                </div>
            ))}
            <div className="lch-mastery-note"><Target size={15} /> Prochaine séance : priorité « {priorite.label} »</div>
        </div>
    );
}

// ---------- Courbe de l'oubli (panneau sombre, dessinée au scroll) ----------
function ForgettingCurve() {
    const ref = useRef(null);
    const [drawn, setDrawn] = useState(false);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const io = new IntersectionObserver(([e]) => {
            if (e.isIntersecting) { setDrawn(true); io.disconnect(); }
        }, { threshold: 0.4 });
        io.observe(el);
        return () => io.disconnect();
    }, []);
    return (
        <div className="lch-curveband" ref={ref}>
            <div className="lch-curveband-copy">
                <span className="ldc-section-no">La preuve en une image</span>
                <h3>La courbe de l'oubli — et comment on la déjoue</h3>
                <p>Sans révision, la trace s'efface en quelques jours. Chaque retour au bon moment la consolide un peu plus longtemps.</p>
            </div>
            <svg viewBox="0 0 520 230" className={`lch-curve ${drawn ? 'is-drawn' : ''}`} role="img"
                aria-label="Sans révision la mémoire décline vite ; des révisions espacées la consolident">
                <line x1="36" y1="196" x2="500" y2="196" className="lch-curve-axis" />
                <path d="M 36 28 C 90 115, 150 160, 260 178 C 350 190, 440 193, 500 194" className="lch-curve-decay" />
                <path d="M 36 28 C 64 70, 82 96, 100 110 L 100 36 C 138 76, 166 90, 194 99 L 194 41 C 250 72, 300 79, 345 83 L 345 46 C 410 58, 470 61, 500 62"
                    className="lch-curve-spaced" pathLength="1000" />
                {[[100, 36, 'J+1'], [194, 41, 'J+3'], [345, 46, 'J+8']].map(([x, y, l], i) => (
                    <g key={l} className="lch-curve-milestone" style={{ transitionDelay: `${0.9 + i * 0.35}s` }}>
                        <circle cx={x} cy={y} r="5.5" className="lch-curve-dot" />
                        <text x={x} y={y - 12} className="lch-curve-dotlabel" textAnchor="middle">{l}</text>
                    </g>
                ))}
                <text x="430" y="185" className="lch-curve-label lch-curve-label--decay">sans révision</text>
                <text x="386" y="50" className="lch-curve-label lch-curve-label--spaced">avec révisions espacées</text>
            </svg>
        </div>
    );
}

// ---------- Satellites (le visuel orbital de Codex, conservé tel quel) ----------
function MemoryVisual() {
    return (
        <div className="ldc-memory-visual" aria-label="Illustration de trois révisions espacées">
            <span className="ldc-orbit ldc-orbit--one" />
            <span className="ldc-orbit ldc-orbit--two" />
            <span className="ldc-orbit ldc-orbit--three" />
            <div className="ldc-memory-core"><small>notion</small><strong>acquise</strong></div>
            <span className="ldc-memory-date ldc-memory-date--one">J+1</span>
            <span className="ldc-memory-date ldc-memory-date--two">J+3</span>
            <span className="ldc-memory-date ldc-memory-date--three">J+8</span>
        </div>
    );
}

const LEARNING_STEPS = [
    ['01', 'Répondre sans filet', 'Tu mobilises réellement ce que tu sais, au lieu de simplement reconnaître une information.'],
    ['02', 'Comprendre le décalage', "La correction explique le raisonnement attendu et transforme l'erreur en point d'appui."],
    ['03', "Revenir avant l'oubli", 'Les notions fragiles reviennent plus vite ; les acquis peuvent progressivement s\'espacer.'],
    ['04', 'Choisir la suite', 'Tu vois les sujets à renforcer avant de lancer ta prochaine séance.'],
];

// ---------- Page ----------
export default function AdminLandingClaudeHybride() {
    const pool = usePool();
    const [stats, setStats] = useState({ questions: null, scopes: null, prixMin: 3 });

    useEffect(() => {
        api.get('/scopes/list.php')
            .then(({ data }) => {
                const scopes = data?.data?.scopes || [];
                const editorial = scopes.filter(s => s.content_source !== 'user');
                const prices = editorial.map(s => Number(s.price_1m)).filter(p => p > 0);
                setStats({
                    questions: editorial.reduce((sum, s) => sum + (s.nb_questions || 0), 0) || null,
                    scopes: editorial.length || null,
                    prixMin: prices.length ? Math.min(...prices) : 3,
                });
            })
            .catch(() => { /* ordres de grandeur en repli */ });
    }, []);

    const questionCount = stats.questions ? stats.questions.toLocaleString('fr-FR') : '≈ 4 000';

    return (
        <div className="ldc lch">
            <div className="ldc-proto-note">
                Hybride Claude · <Link to="/">home (hybride Codex, promu)</Link> · <Link to="/landinghybridecompare">comparatif</Link>
                {pool === POOL_ATSEM && <> · pool détecté : ATSEM</>}
            </div>

            {/* ===== HERO photographique (Codex) ===== */}
            <section className="ldc-hero">
                <img src="/images/landing/monquiz-learning-hero.png" alt="Candidat préparant son concours avec une tablette et des fiches de révision" />
                <div className="ldc-hero-shade" />
                <div className="ldc-hero-copy">
                    <BrandLogo width={170} hideTagline monColor="#fff" className="ldc-hero-logo" />
                    <span className="ldc-kicker"><i /> Préparer un concours autrement</span>
                    <h1>Ne révise plus<br />au hasard.<br /><em>Retiens plus.</em></h1>
                    <p>Des questions qui te mettent vraiment à l'épreuve, des corrections qui font comprendre et une progression qui indique quoi travailler ensuite.</p>
                    <div className="ldc-actions">
                        <a href="#essai" className="ldc-btn ldc-btn--light">Faire l'audit express <ArrowRight size={17} /></a>
                        <a href="#methode" className="ldc-text-link">Voir la méthode</a>
                    </div>
                    <small>Sans carte bancaire · l'accès complet nécessite un abonnement</small>
                </div>
                <HeroLiveCard />
            </section>

            <div className="ldc-facts" aria-label="Repères MonQuiz">
                <span><strong>{questionCount}</strong> questions validées</span>
                <i />
                <span><strong>{stats.scopes ?? 7}</strong> thématiques de concours</span>
                <i />
                <span><strong>3 IA + 1 humain</strong> pour croiser la relecture</span>
            </div>

            {/* ===== 01 — Méthode ===== */}
            <section className="ldc-manifesto" id="methode">
                <span className="ldc-section-no">01 — La méthode</span>
                <h2>Un score mesure un instant.<br /><em>Une bonne séance prépare la suivante.</em></h2>
                <p>MonQuiz ne s'arrête pas au verdict « bon » ou « faux ». Chaque réponse alimente une boucle simple, visible et utile.</p>
            </section>

            <section className="ldc-journey">
                {LEARNING_STEPS.map(([num, title, text]) => (
                    <article key={num}>
                        <span>{num}</span>
                        <div><h3>{title}</h3><p>{text}</p></div>
                    </article>
                ))}
            </section>

            {/* ===== 02 — Mini-audit (6 questions mélangées, pool selon provenance) ===== */}
            <section className="ldc-try" id="essai">
                <div className="ldc-try-copy">
                    <span className="ldc-section-no">02 — À toi de jouer</span>
                    <h2>Deux minutes pour savoir quoi travailler.</h2>
                    <p>
                        Six vraies questions, mélangées sur plusieurs sujets et corrigées comme dans
                        l'application. À la fin, un bilan par sujet te dit où tu es solide — et par
                        quoi commencer.
                    </p>
                    <span className="ldc-handnote">Fais l'audit express →</span>
                </div>
                <MiniQuiz pool={pool} />
            </section>

            {/* ===== 03 — Contre l'oubli : satellites (Codex) + courbe (Claude) ===== */}
            <section className="ldc-memory">
                <div className="ldc-memory-copy">
                    <span className="ldc-section-no">03 — Contre l'oubli</span>
                    <h2>Revenir au bon moment, pas tout recommencer.</h2>
                    <p>Une erreur revient rapidement. Une notion maîtrisée peut attendre davantage. La répétition espacée concentre l'effort là où il produit le plus d'effet.</p>
                    <ul>
                        <li><Check size={16} /> Révisions calculées notion par notion</li>
                        <li><Check size={16} /> Quiz dédiés à tes erreurs</li>
                        <li><Check size={16} /> Rythme adaptable à ta préparation</li>
                    </ul>
                </div>
                <MemoryVisual />
            </section>

            <ForgettingCurve />

            {/* ===== 04 — Direction ===== */}
            <section className="ldc-direction">
                <div className="ldc-direction-copy">
                    <span className="ldc-section-no">04 — Une direction claire</span>
                    <h2>Ta moyenne ne raconte pas toute l'histoire.</h2>
                    <p>La progression par sujet distingue les acquis, les fragilités et ce que tu as encore peu travaillé. Tu peux enfin choisir ta prochaine séance avec une raison.</p>
                    <Link to="/inscription">Créer mon compte gratuit <ArrowRight size={16} /></Link>
                </div>
                <MasteryCard pool={pool} />
            </section>

            {/* ===== 05 — Accès ===== */}
            <section className="ldc-access lch-access">
                <div className="lch-access-panel">
                    <div className="lch-access-head">
                        <span className="ldc-section-no">05 — Sans petite ligne cachée</span>
                        <h2>Essayer gratuitement.<br />Débloquer quand tu es prêt.</h2>
                        <p>Aucune carte bancaire pour commencer, aucune reconduction automatique : tu paies une fois, pour la durée que tu choisis.</p>
                    </div>
                    <div className="lch-access-tiers">
                        <div className="lch-access-tier">
                            <span className="lch-access-tag">Essai gratuit</span>
                            <strong className="lch-access-price">0 €<small>sans carte bancaire</small></strong>
                            <ul>
                                <li><Check size={15} /> Sujets de démonstration sur chaque thématique</li>
                                <li><Check size={15} /> Corrections détaillées après chaque question</li>
                                <li><Check size={15} /> Premier aperçu de ta progression</li>
                            </ul>
                        </div>
                        <div className="lch-access-tier lch-access-tier--pro">
                            <span className="lch-access-tag">Accès complet</span>
                            <strong className="lch-access-price">dès {stats.prixMin} €<small>/ mois · paiement unique</small></strong>
                            <ul>
                                <li><Check size={15} /> Toute la thématique, sans limite</li>
                                <li><Check size={15} /> Révisions espacées &amp; QCM mélangés (proches d'un examen blanc)</li>
                                <li><Check size={15} /> Quiz d'erreurs ciblés sur tes fragilités</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* ===== Final ===== */}
            <section className="ldc-final">
                <span>Prêt pour une séance qui sert vraiment la suivante ?</span>
                <h2>Commence par un sujet d'essai.<br /><em>Vois ce que tu sais vraiment.</em></h2>
                <div className="ldc-actions ldc-actions--center">
                    <Link to="/open" className="ldc-btn ldc-btn--dark">Essayer gratuitement <ArrowRight size={17} /></Link>
                    <Link to="/inscription" className="ldc-text-link">Créer un compte</Link>
                </div>
            </section>
        </div>
    );
}
